import { Pages } from '../Pages'
import { useEffect, useRef, useState } from 'react'
import { MenuItems } from '../enums/MenuItems'
import roleApi from '../api/role/RoleApi'
import { RoleLiteDto } from '../api/role/models/RoleDto'
import LoadingSpinner from '../components/LoadingSpinner'
import { NoteType } from '../enums/NoteType'
import Note from '../components/Note'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import Button from '../components/Button'
import Section from '../components/Section'
import NoRecords from '../components/NoRecords'
import Cards from '../components/Cards'
import RoleCard from '../components/RoleCard'
import { AuthHelper } from '../auth/AuthHelper'
import { UserRoles } from '../api/tenant/models/UserDto'
import { useSafeUserData } from '../stateStores/User'
import { MapExtra } from '../utils/MapExtra'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import personApi from '../api/person/PersonApi'
import { SetExtra } from '../utils/SetExtra'
import LayoutSingleView from '../components/layout/LayoutSingleView'

function Roles(): React.JSX.Element {
    const [roles, setRoles] = useState<RoleLiteDto[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const { user } = useSafeUserData()
    const personCacheRef = useRef(new MapExtra<string, PersonLiteDto>())

    useEffect(() => {
        const fetchData = async () => {
            try {
                const newRoles = (
                    await roleApi.getAllLite({
                        includeHiringTeamIds: true,
                        includeNetworkLinkIds: false,
                        includeCompanyLinkIds: false,
                    })
                ).roles

                const personIds = new SetExtra<string>(
                    newRoles.flatMap((role) => role.hiringTeamIds ?? []),
                )

                const persons = (
                    await personApi.getPersonsLite({
                        personIds: personIds.getValuesAsArray(),
                    })
                ).persons

                personCacheRef.current = new MapExtra<string, PersonLiteDto>()
                for (const person of persons) {
                    personCacheRef.current.set(person.id, person)
                }

                setRoles(newRoles)
            } catch (error) {
                console.error(error)
                const msg =
                    'There was a problem loading roles. Please refresh the page to try again. Error details: '
                if (error instanceof Error) {
                    setErrMsg(`${msg}${error.message}`)
                } else {
                    // Handle non-Error objects (e.g., strings or custom error shapes)
                    setErrMsg(`${msg}An unknown error occurred.`)
                }
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [])

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <LoadingSpinner />
            </LayoutSingleView>
        )
    }

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Note type={NoteType.ERROR}>{errMsg}</Note>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section
                title="Roles"
                buttons={
                    roles.length > 0 &&
                    AuthHelper.userHasRole(user, UserRoles.TA) && (
                        <Button
                            style="primary"
                            to={Pages.CreateNewRole}
                            type="link"
                        >
                            <FontAwesomeIcon
                                icon={faPlusCircle}
                                className={`mr-2`}
                            />
                            New Role
                        </Button>
                    )
                }
            >
                {roles.length === 0 &&
                    (AuthHelper.userHasRole(user, UserRoles.TA) ? (
                        <NoRecords
                            imgSrc="job_role.svg"
                            caption="Add a job role to start finding candidates"
                        >
                            <Button
                                style="primary"
                                type="link"
                                classes="mt-4"
                                to={Pages.CreateNewRole}
                            >
                                <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="mr-2"
                                />
                                New Role
                            </Button>
                        </NoRecords>
                    ) : (
                        <NoRecords
                            imgSrc="job_role.svg"
                            caption="No job roles available - has the TA added you to the hiring team for a job role?"
                        >
                            <></>
                        </NoRecords>
                    ))}
                {roles.length > 0 && (
                    <Cards className="mt-4 bg-gray-50 p-4 rounded-lg flex-wrap">
                        {roles.map((role) => (
                            <RoleCard
                                role={role}
                                key={`role-card-${role.id}`}
                                personCache={personCacheRef.current}
                            />
                        ))}
                    </Cards>
                )}
            </Section>
        </LayoutSingleView>
    )
}

export default Roles
